import React from "react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useSurveys, useSurveyAnalytics } from "../hooks/useSurveys";
import { useDashboardTabState } from "../hooks/useDashboardTabState";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { DashboardTabChrome, DashboardSurveysTabSkeleton } from "./dashboard/DashboardTabChrome";
import { getDashboardMetricInsights } from "../api/client";
import { useTranslation } from "react-i18next";
import { formatDashboardUserError } from "../utils/formatDashboardUserError";
import { useConsolidatedDashboardContext } from "../hooks/useConsolidatedDashboardContext";

const toPercentNumber = (value) => {
  if (value == null) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  // Backend/legacy may send completionRate as ratio (0..1) or as percent (0..100)
  return n <= 1 ? n * 100 : n;
};

const formatPercent0 = (value) => {
  const pct = toPercentNumber(value);
  if (pct == null) return "—";
  return `${Math.round(pct)}%`;
};

const SurveyAnalysis = ({
  start,
  end,
  channel = "",
  originId = null,
  presentationSnapshot = null,
  readOnly = false,
  surveyId: presentationSurveyId = null,
  surveyTitle: presentationSurveyTitle = null,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("settings");
  const consolidatedCtx = useConsolidatedDashboardContext();
  const isConsolidatedSurveys = Boolean(consolidatedCtx?.surveysEnabled);
  const listQ = useSurveys(1, 200, "", "", { enabled: !readOnly });
  const surveys = listQ.data?.surveys || [];

  const [selectedSurveyId, setSelectedSurveyId] = React.useState(
    presentationSurveyId ? String(presentationSurveyId) : ""
  );
  const [selectedQuestionId, setSelectedQuestionId] = React.useState("");

  React.useEffect(() => {
    if (readOnly && presentationSurveyId) {
      setSelectedSurveyId(String(presentationSurveyId));
    }
  }, [readOnly, presentationSurveyId]);

  React.useEffect(() => {
    if (readOnly) return;
    if (!surveys.length || selectedSurveyId) return;
    const fromUrl = searchParams.get("survey");
    const initial =
      fromUrl && surveys.some((s) => String(s.id) === fromUrl)
        ? fromUrl
        : String(surveys[0].id);
    setSelectedSurveyId(initial);
  }, [surveys, selectedSurveyId, searchParams]);

  React.useEffect(() => {
    const fromUrl = searchParams.get("survey");
    if (!fromUrl || !surveys.length) return;
    if (!surveys.some((s) => String(s.id) === fromUrl)) return;
    if (fromUrl !== selectedSurveyId) {
      setSelectedSurveyId(fromUrl);
      setSelectedQuestionId("");
    }
  }, [searchParams, surveys, selectedSurveyId]);

  const analyticsFilters = useMemo(
    () => ({
      startDate: start || undefined,
      endDate: end || undefined,
      ...(channel ? { channel } : {}),
      ...(originId ? { originId } : {}),
    }),
    [start, end, channel, originId]
  );

  const analyticsQ = useSurveyAnalytics(
    selectedSurveyId,
    Boolean(selectedSurveyId) && !presentationSnapshot,
    analyticsFilters
  );
  const analyticsTab = useDashboardTabState(
    presentationSnapshot
      ? { data: presentationSnapshot, isLoading: false, error: null, isFetching: false }
      : analyticsQ,
    { registerShare: !readOnly && !presentationSnapshot }
  );
  const analytics = analyticsTab.data;

  const questions = analytics?.questions || [];

  React.useEffect(() => {
    if (!selectedQuestionId && questions.length) {
      setSelectedQuestionId(String(questions[0].questionId));
    }
  }, [selectedQuestionId, questions]);

  const selectedQuestion =
    questions.find((q) => String(q.questionId) === String(selectedQuestionId)) || questions[0] || null;

  const chartData = useMemo(
    () =>
      (selectedQuestion?.options || []).map((opt) => ({
        name: opt.label || opt.value || "Opção",
        respostas: Number(opt.count || 0),
      })),
    [selectedQuestion]
  );

  const executive = analytics?.executive || {};
  const scoreSummary = executive.scoreSummary || {};
  const startedCount = Number(executive.startedCount || 0);
  const completedCount = Number(executive.completedCount || 0);
  const completionRate = executive.completionRate ?? null;
  const openTextResponses = analytics?.openTextResponses || [];

  const loading =
    listQ.isLoading || (Boolean(selectedSurveyId) && analyticsTab.showSkeletons);
  const isInitialLoading = loading;
  const listErrorMessage = listQ.error ? formatDashboardUserError(listQ.error, t) : null;
  const errorMessage = listErrorMessage || analyticsTab.errorMessage;
  const analyticsReady = Boolean(selectedSurveyId) && analytics != null;
  const showContent = !loading && !errorMessage;
  const previewPendingMessage = t("dashboardPage.shared.previewSectionPending");

  const selectedSurveyTitle = useMemo(() => {
    if (presentationSurveyTitle) return presentationSurveyTitle;
    const s = surveys.find((x) => String(x.id) === String(selectedSurveyId));
    if (!s) return "";
    return String(s.title || s.titulo || "").trim() || `Pesquisa ${selectedSurveyId}`;
  }, [surveys, selectedSurveyId, presentationSurveyTitle]);

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);

  const surveySummaryContext = useMemo(
    () => ({ surveyTitle: selectedSurveyTitle }),
    [selectedSurveyTitle]
  );

  const metricInsightsMutation = useMutation(
    async (cfg) =>
      getDashboardMetricInsights("surveys", {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: { start, end },
        summaryContext: { ...surveySummaryContext, ...(cfg.summaryContext || {}) },
        filters: cfg.filters || {},
      }),
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => (
    <Tooltip title="Explicar com IA">
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label="Explicar com IA">
        <AutoAwesomeIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={analyticsTab.isDeferred}
        isPreview={analyticsTab.isPreview}
        isInitialLoading={isInitialLoading}
        errorMessage={errorMessage}
        previewPendingMessage={previewPendingMessage}
        emptyState={
          showContent && !surveys.length
            ? {
                visible: true,
                title: t("dashboardPage.surveys.emptyListTitle", {
                  defaultValue: "Nenhuma pesquisa cadastrada ainda",
                }),
                hint: isConsolidatedSurveys
                  ? t("dashboardPage.surveys.emptyListHintPartner", {
                      defaultValue:
                        "Nenhuma pesquisa nos workspaces selecionados. Ajuste o filtro de workspaces ou crie pesquisas nos clientes.",
                    })
                  : t("dashboardPage.surveys.emptyListHint", {
                      defaultValue:
                        "Crie uma pesquisa no menu Pesquisas para acompanhar os resultados aqui.",
                    }),
              }
            : null
        }
      />
      {!readOnly ? (
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        {listQ.isLoading ? (
          <Skeleton variant="rounded" width={280} height={40} />
        ) : (
          <Select
            value={selectedSurveyId || ""}
            onChange={(e) => {
              const id = String(e.target.value);
              setSelectedSurveyId(id);
              setSelectedQuestionId("");
              const next = new URLSearchParams(searchParams);
              if (id) next.set("survey", id);
              else next.delete("survey");
              setSearchParams(next, { replace: true });
            }}
            sx={{ minWidth: 280 }}
            size="small"
          >
            {surveys.map((survey) => (
              <MenuItem key={survey.id} value={String(survey.id)}>
                {survey.title || survey.titulo || `Pesquisa #${survey.id}`}
              </MenuItem>
            ))}
          </Select>
        )}

        <Button
          variant="outlined"
          disabled={!selectedSurveyId || loading}
          onClick={() => navigate(`/surveys/${selectedSurveyId}/analytics`)}
        >
          Abrir analytics
        </Button>
      </Box>
      ) : null}

      {loading && !errorMessage ? <DashboardSurveysTabSkeleton /> : null}

      {showContent && analyticsReady ? (
        <Grid container spacing={3} sx={{ marginBottom: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Iniciadas
                  </Typography>
                  {selectedSurveyId ? (
                    <AiBtn
                      cfg={{
                        metric: "surveys_dash_started",
                        metricName: "Iniciadas",
                        metricDefinition:
                          "Quantidade de vezes que a pesquisa foi iniciada no período do workspace (contexto do dashboard).",
                        metricValue: startedCount,
                        metricHuman: startedCount.toLocaleString("pt-BR"),
                      }}
                    />
                  ) : null}
                </Box>
                <Typography variant="h4" color="primary">
                  {startedCount.toLocaleString("pt-BR")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Concluídas
                  </Typography>
                  {selectedSurveyId ? (
                    <AiBtn
                      cfg={{
                        metric: "surveys_dash_completed",
                        metricName: "Concluídas",
                        metricDefinition:
                          "Quantidade de respostas concluídas (pesquisa finalizada) no contexto agregado exibido.",
                        metricValue: completedCount,
                        metricHuman: completedCount.toLocaleString("pt-BR"),
                      }}
                    />
                  ) : null}
                </Box>
                <Typography variant="h4" color="success.main">
                  {completedCount.toLocaleString("pt-BR")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    Taxa de conclusão
                  </Typography>
                  {selectedSurveyId ? (
                    <AiBtn
                      cfg={{
                        metric: "surveys_dash_completion_rate",
                        metricName: "Taxa de conclusão",
                        metricDefinition:
                          "Razão entre concluídas e iniciadas (ou métrica equivalente exibida no painel).",
                        metricValue:
                          completionRate == null ? null : toPercentNumber(completionRate),
                        metricHuman:
                          formatPercent0(completionRate),
                        summaryContext: { mainScore: scoreSummary.mainValue ?? null },
                      }}
                    />
                  ) : null}
                </Box>
                <Typography variant="h4" color="text.primary">
                  {formatPercent0(completionRate)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Score principal: {scoreSummary.mainValue ?? "—"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Perguntas e Respostas */}
      {showContent && analyticsReady && !analyticsTab.isPreview && questions.length ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
                Perguntas
              </Typography>
              {selectedQuestion && chartData.length ? (
                <AiBtn
                  cfg={{
                    metric: "surveys_dash_question_dist",
                    metricName:
                      selectedQuestion?.title || selectedQuestion?.text || "Distribuição da pergunta",
                    metricDefinition:
                      "Distribuição de respostas por opção para a pergunta selecionada na pesquisa do workspace.",
                    metricValue: chartData.map((row) => ({
                      name: row.name,
                      value: row.respostas,
                    })),
                    metricHuman: `${chartData.length} opções`,
                    summaryContext: {
                      questionPreview: String(
                        selectedQuestion?.title || selectedQuestion?.text || ""
                      ).slice(0, 240),
                    },
                  }}
                />
              ) : null}
              <Select
                value={selectedQuestionId || ""}
                size="small"
                sx={{ minWidth: 320 }}
                onChange={(e) => setSelectedQuestionId(String(e.target.value))}
              >
                {questions.map((q) => (
                  <MenuItem key={q.questionId} value={String(q.questionId)}>
                    {q.title || q.text || `Pergunta #${q.questionId}`}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              {selectedQuestion?.title || selectedQuestion?.text || "Distribuição"}
            </Typography>

            {chartData.length ? (
              <Box sx={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Bar dataKey="respostas" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="warning">Sem dados suficientes para essa pergunta ainda.</Alert>
            )}
          </CardContent>
        </Card>
      ) : null}

      {showContent && analyticsReady && !analyticsTab.isPreview ? (
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                Respostas abertas (amostra)
              </Typography>
              {selectedSurveyId ? (
                <AiBtn
                  cfg={{
                    metric: "surveys_dash_open_text_sample",
                    metricName: "Respostas abertas (amostra)",
                    metricDefinition:
                      "Amostra de textos livres enviados nas pesquisas (trechos agregados, sem identificação de respondente).",
                    metricValue: (openTextResponses || []).slice(0, 18).map((row) =>
                      String(row.answerText || row.text || row.answer || "").slice(0, 280)
                    ),
                    metricHuman: `${(openTextResponses || []).length} no total (amostra na tela)`,
                  }}
                />
              ) : null}
            </Box>
            {(openTextResponses || []).length ? (
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {openTextResponses.slice(0, 10).map((row, idx) => (
                  <li key={`${row.sessionId || "s"}-${row.questionId || "q"}-${idx}`}>
                    <Typography variant="body2">{row.answerText || row.text || row.answer || "—"}</Typography>
                  </li>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Ainda não há respostas abertas.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : null}

      {!readOnly ? (
      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="surveys"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
      ) : null}
    </Box>
  );
};

export default SurveyAnalysis;
